import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, addDoc, onSnapshot, query, 
  where, doc, updateDoc, getDoc, Timestamp, orderBy, deleteDoc 
} from 'firebase/firestore';
import Swal from 'sweetalert2';

function Purchaseentry() {
  const [vendors, setVendors] = useState([]);
  const [lubricants, setLubricants] = useState([]);
  const [purchases, setPurchases] = useState([]);
  
  // UI State
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Master Form State
  const [vendorId, setVendorId] = useState("");
  const [billNo, setBillNo] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [items, setItems] = useState([{ itemId: '', itemName: '', qty: 0, rate: 0, total: 0 }]);

  const userId = auth.currentUser?.uid;
  const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });

  useEffect(() => {
    if (!userId) return;
    const qV = query(collection(db, "vendors"), where("userId", "==", userId));
    const unsubV = onSnapshot(qV, (snap) => setVendors(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

    const qL = query(collection(db, "lubricants"), where("userId", "==", userId));
    const unsubL = onSnapshot(qL, (snap) => setLubricants(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

    const qP = query(collection(db, "purchases"), where("userId", "==", userId), orderBy("createdAt", "desc"));
    const unsubP = onSnapshot(qP, (snap) => setPurchases(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

    return () => { unsubV(); unsubL(); unsubP(); };
  }, [userId]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    if (field === 'itemId') {
        const selectedProd = lubricants.find(l => l.id === value);
        newItems[index].itemId = value;
        newItems[index].itemName = selectedProd ? selectedProd.name : "";
        newItems[index].rate = selectedProd ? selectedProd.price : 0;
    } else {
        newItems[index][field] = value;
    }
    const q = parseFloat(newItems[index].qty) || 0;
    const r = parseFloat(newItems[index].rate) || 0;
    newItems[index].total = q * r;
    setItems(newItems);
  };

  const addRow = () => setItems([...items, { itemId: '', itemName: '', qty: 0, rate: 0, total: 0 }]);
  const removeRow = (index) => setItems(items.filter((_, i) => i !== index));
  const grandTotal = items.reduce((sum, item) => sum + item.total, 0);

  // --- REUSABLE STOCK ADJUSTMENT LOGIC ---
  const adjustStockAndBalance = async (purchaseData, type) => {
    const multiplier = type === 'add' ? 1 : -1;
    
    // 1. Update Product Stocks
    for (const item of purchaseData.items) {
      if (item.itemId) {
        const prodRef = doc(db, "lubricants", item.itemId);
        const prodSnap = await getDoc(prodRef);
        if (prodSnap.exists()) {
          const currentStock = parseFloat(prodSnap.data().stock) || 0;
          await updateDoc(prodRef, { stock: currentStock + (parseFloat(item.qty) * multiplier) });
        }
      }
    }

    // 2. Update Vendor Balance
    const vRef = doc(db, "vendors", purchaseData.vendorId);
    const vSnap = await getDoc(vRef);
    if (vSnap.exists()) {
      const currentBal = parseFloat(vSnap.data().currentBalance) || 0;
      await updateDoc(vRef, { currentBalance: currentBal + (purchaseData.grandTotal * multiplier) });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const purchaseData = { userId, vendorId, billNo, date, items, grandTotal, createdAt: Timestamp.now() };

      if (editId) {
        // Find the old record first to reverse its impact
        const oldDoc = purchases.find(p => p.id === editId);
        await adjustStockAndBalance(oldDoc, 'remove'); // Remove old stock/balance
        await updateDoc(doc(db, "purchases", editId), purchaseData); // Update record
        await adjustStockAndBalance(purchaseData, 'add'); // Add new stock/balance
        Toast.fire({ icon: 'success', title: 'Purchase Updated' });
      } else {
        await addDoc(collection(db, "purchases"), purchaseData);
        await adjustStockAndBalance(purchaseData, 'add');
        Toast.fire({ icon: 'success', title: 'Purchase Recorded' });
      }

      resetForm();
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally { setLoading(false); }
  };

  const handleDelete = async (purchase) => {
    const result = await Swal.fire({
      title: 'Delete Bill?',
      text: "Stock and Vendor balance will be reversed!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        await adjustStockAndBalance(purchase, 'remove');
        await deleteDoc(doc(db, "purchases", purchase.id));
        Toast.fire({ icon: 'success', title: 'Purchase Deleted' });
      } catch (err) {
        Toast.fire({ icon: 'error', title: 'Failed to delete' });
      }
    }
  };

  const resetForm = () => {
    setItems([{ itemId: '', itemName: '', qty: 0, rate: 0, total: 0 }]);
    setBillNo("");
    setVendorId("");
    setEditId(null);
    setShowForm(false);
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setVendorId(p.vendorId);
    setBillNo(p.billNo);
    setDate(p.date);
    setItems(p.items);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const filtered = purchases.filter(p => 
    p.billNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendors.find(v => v.id === p.vendorId)?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentItems = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="content-wrapper bg-light min-vh-100">
      <section className="content-header py-3 px-4">
        <div className="d-flex justify-content-between align-items-center bg-white p-3 shadow-sm rounded border-left-primary">
            <h4 className="m-0 font-weight-bold text-navy"><i className="fas fa-truck-loading mr-2"></i>Purchase Entry</h4>
            <button className={`btn btn-sm ${showForm ? 'btn-danger' : 'btn-primary'} shadow-sm`} onClick={() => showForm ? resetForm() : setShowForm(true)}>
                {showForm ? <><i className="fas fa-times mr-1"></i> Cancel</> : <><i className="fas fa-plus mr-1"></i> Add Purchase Bill</>}
            </button>
        </div>
      </section>

      <section className="content px-4">
        {showForm && (
            <div className="card border-0 shadow-sm mb-4 animate__animated animate__fadeIn">
                <form onSubmit={handleSubmit}>
                    <div className="card-header bg-navy text-white py-2">
                        <h6 className="m-0">{editId ? 'Edit Purchase Bill' : 'New Purchase Details'}</h6>
                    </div>
                    <div className="card-body">
                        <div className="row mb-3">
                            <div className="col-md-4">
                                <label className="small font-weight-bold">Vendor</label>
                                <select className="form-control" value={vendorId} onChange={e => setVendorId(e.target.value)} required>
                                    <option value="">-- Select Vendor --</option>
                                    {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4"><label className="small font-weight-bold">Bill No</label><input type="text" className="form-control" value={billNo} onChange={e => setBillNo(e.target.value)} required /></div>
                            <div className="col-md-4"><label className="small font-weight-bold">Date</label><input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} /></div>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-bordered table-sm text-sm">
                                <thead className="bg-light">
                                    <tr><th>Product</th><th width="120">Qty</th><th width="120">Rate</th><th width="120">Total</th><th width="40"></th></tr>
                                </thead>
                                <tbody>
                                    {items.map((item, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <select className="form-control form-control-sm border-0" value={item.itemId} onChange={e => handleItemChange(idx, 'itemId', e.target.value)} required>
                                                    <option value="">-- Choose Product --</option>
                                                    {lubricants.map(l => <option key={l.id} value={l.id}>{l.name} - {l.grade}</option>)}
                                                </select>
                                            </td>
                                            <td><input type="number" step="any" className="form-control form-control-sm border-0" value={item.qty} onChange={e => handleItemChange(idx, 'qty', e.target.value)} required /></td>
                                            <td><input type="number" step="any" className="form-control form-control-sm border-0" value={item.rate} onChange={e => handleItemChange(idx, 'rate', e.target.value)} required /></td>
                                            <td className="bg-light text-right font-weight-bold pt-2">₹ {item.total.toLocaleString()}</td>
                                            <td className="text-center">{items.length > 1 && <i className="fas fa-minus-circle text-danger mt-2 cursor-pointer" onClick={() => removeRow(idx)}></i>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <button type="button" onClick={addRow} className="btn btn-xs btn-outline-info"><i className="fas fa-plus mr-1"></i> Add Row</button>
                        <div className="text-right mt-3"><h4 className="text-primary font-weight-bold">Total: ₹ {grandTotal.toLocaleString()}</h4></div>
                    </div>
                    <div className="card-footer bg-white text-right">
                        <button type="submit" disabled={loading} className="btn btn-success px-5 shadow-sm">{loading ? 'Processing...' : 'Save Bill'}</button>
                    </div>
                </form>
            </div>
        )}

        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
            <h6 className="m-0 font-weight-bold text-navy">Purchase History</h6>
            <input type="text" className="form-control form-control-sm w-25" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <div className="card-body p-0">
            <table className="table table-hover text-sm mb-0">
                <thead className="bg-light"><tr><th width="50">Sr.</th><th>Date</th><th>Vendor</th><th>Bill No</th><th className="text-right">Amount</th><th className="text-center">Action</th></tr></thead>
                <tbody>
                    {currentItems.map((p, i) => (
                        <tr key={p.id}>
                            <td className="text-center">{(currentPage - 1) * itemsPerPage + i + 1}</td>
                            <td>{p.date}</td>
                            <td className="font-weight-bold">{vendors.find(v => v.id === p.vendorId)?.name || '...'}</td>
                            <td><span className="badge badge-light border">{p.billNo}</span></td>
                            <td className="text-right font-weight-bold text-navy">₹ {p.grandTotal.toLocaleString()}</td>
                            <td className="text-center">
                                <button className="btn btn-link btn-sm text-info mr-2" onClick={() => startEdit(p)}><i className="fas fa-edit"></i></button>
                                <button className="btn btn-link btn-sm text-danger" onClick={() => handleDelete(p)}><i className="fas fa-trash-alt"></i></button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
          </div>
          <div className="card-footer bg-white d-flex justify-content-between py-2">
            <small className="text-muted">Showing {currentItems.length} of {filtered.length} entries</small>
            <div className="btn-group"><button className="btn btn-xs btn-outline-secondary" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Prev</button><button className="btn btn-xs btn-outline-secondary" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Next</button></div>
          </div>
        </div>
      </section>
      <style dangerouslySetInnerHTML={{ __html: `.bg-navy { background: #2f3f00; } .text-navy { color: #001f3f; } .cursor-pointer { cursor: pointer; }` }} />
    </div>
  );
}

export default Purchaseentry;